import { z } from 'zod';
import type { AnalyticsOption } from './normalize';
import type { CrosstabCell, Group, PairComparison, ScatterPoint } from './compare';
import { captionFor } from './caption';
import type { QuestionSummary } from './summarize';

/**
 * Chart payloads that travel outside the browser: from v1-api through Ask
 * hollie's stream into saved threads. They are plain JSON (no Maps), carry no
 * raw value arrays or transcripts, and are re-validated on every read because
 * saved threads are stored data.
 */

/** A question referenced by a comparison. */
export interface QuestionRef {
  id: string;
  label: string;
}

/** A pair comparison as plain JSON. */
export type ComparisonPayload =
  | { kind: 'crosstab'; a: QuestionRef; b: QuestionRef; n: number; rows: AnalyticsOption[]; columns: AnalyticsOption[]; rowTotals: number[]; cells: CrosstabCell[][] }
  | { kind: 'scatter'; a: QuestionRef; b: QuestionRef; n: number; points: ScatterPoint[]; pearson: number | null; spearman: number | null }
  | { kind: 'groups'; category: QuestionRef; value: QuestionRef; n: number; groups: Group[] };

/** One chart card: a question summary or a comparison. */
export type ChartPayload =
  | { kind: 'question'; summary: QuestionSummary }
  | { kind: 'comparison'; comparison: ComparisonPayload };

/** A question summary as a payload, without the raw value array behind numbers. */
export function toQuestionPayload(summary: QuestionSummary): ChartPayload {
  return { kind: 'question', summary: summary.kind === 'numeric' ? { ...summary, values: [] } : summary };
}

/** A pair comparison as a payload, keeping only question ids and labels. */
export function toComparisonPayload(pair: PairComparison): ChartPayload {
  const ref = (question: { id: string; label: string }): QuestionRef => ({ id: question.id, label: question.label });
  switch (pair.kind) {
    case 'crosstab':
      return { kind: 'comparison', comparison: { kind: 'crosstab', a: ref(pair.a.question), b: ref(pair.b.question), n: pair.n, rows: pair.rows, columns: pair.columns, rowTotals: pair.rowTotals, cells: pair.cells } };
    case 'scatter':
      return { kind: 'comparison', comparison: { kind: 'scatter', a: ref(pair.a.question), b: ref(pair.b.question), n: pair.n, points: pair.points, pearson: pair.pearson, spearman: pair.spearman } };
    default:
      return { kind: 'comparison', comparison: { kind: 'groups', category: ref(pair.category.question), value: ref(pair.value.question), n: pair.n, groups: pair.groups } };
  }
}

/** Bounds that keep a stored payload small and renderable. */
const MAX_ITEMS = 500;
const MAX_TEXT = 2_000;
const MAX_VALUES = 20_000;

const count = z.number().int().nonnegative();
const finite = z.number().finite();
const share = z.number().finite().min(0).max(1);
const text = z.string().max(MAX_TEXT);
const nullableNumber = finite.nullable();
const option = z.object({ key: text, label: text });
const item = z.object({ key: text, label: text, count, share });
const ref = z.object({ id: text, label: text });

const base = {
  fieldId: text,
  fieldType: z.string().max(60),
  label: text,
  answered: count,
  eligible: count,
  excluded: count,
  display: z.enum(['chart', 'line', 'text']),
};

const summarySchema = z.discriminatedUnion('kind', [
  z.object({
    ...base,
    kind: z.literal('distribution'),
    view: z.enum(['donut', 'bars']),
    order: z.enum(['author', 'count']),
    multi: z.boolean(),
    source: z.enum(['choice', 'boolean', 'address', 'text']),
    items: z.array(item).max(MAX_ITEMS),
    rest: z.object({ count, options: count }).optional(),
    selections: z.object({ mean: finite, min: count, max: count }).optional(),
    otherTexts: z.array(z.string().max(10_000)).max(MAX_ITEMS),
    medianKey: text.optional(),
  }),
  z.object({
    ...base,
    kind: z.literal('numeric'),
    source: z.enum(['number', 'text']),
    values: z.array(finite).max(MAX_VALUES),
    n: count,
    mean: nullableNumber,
    median: nullableNumber,
    min: nullableNumber,
    max: nullableNumber,
    stdDev: nullableNumber,
    q1: nullableNumber,
    q3: nullableNumber,
    histogram: z.object({ kind: z.enum(['values', 'ranges']), bins: z.array(z.object({ x0: finite, x1: finite, count })).max(MAX_ITEMS) }),
    scale: z.object({ lowLabel: text, highLabel: text, direction: z.enum(['ascending', 'descending']) }).optional(),
  }),
  z.object({
    ...base,
    kind: z.literal('nps'),
    promoters: count,
    passives: count,
    detractors: count,
    score: finite,
    distribution: z.array(z.object({ value: finite, count })).max(11),
  }),
  z.object({
    ...base,
    kind: z.literal('ranking'),
    items: z.array(z.object({ key: text, label: text, averageRank: finite, firstPlace: count })).max(MAX_ITEMS),
  }),
  z.object({
    ...base,
    kind: z.literal('matrix'),
    columns: z.array(option).max(50),
    rows: z.array(z.object({ key: text, label: text, answered: count, cells: z.array(item).max(50) })).max(MAX_ITEMS),
  }),
  z.object({
    ...base,
    kind: z.literal('timeline'),
    granularity: z.enum(['day', 'week', 'month']),
    bins: z.array(z.object({ start: z.string().max(10), count })).max(MAX_ITEMS),
  }),
  z.object({ ...base, kind: z.literal('count') }),
]);

const groupSchema = z.object({
  key: text, label: text, n: count, values: z.array(finite).max(MAX_VALUES),
  median: nullableNumber, q1: nullableNumber, q3: nullableNumber, min: nullableNumber, max: nullableNumber,
});

const comparisonSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('crosstab'), a: ref, b: ref, n: count,
    rows: z.array(option).max(MAX_ITEMS), columns: z.array(option).max(MAX_ITEMS), rowTotals: z.array(count).max(MAX_ITEMS),
    cells: z.array(z.array(z.object({ rowKey: text, columnKey: text, count, rowShare: share })).max(MAX_ITEMS)).max(MAX_ITEMS),
  }),
  z.object({
    kind: z.literal('scatter'), a: ref, b: ref, n: count,
    points: z.array(z.object({ x: finite, y: finite, count })).max(MAX_VALUES), pearson: nullableNumber, spearman: nullableNumber,
  }),
  z.object({ kind: z.literal('groups'), category: ref, value: ref, n: count, groups: z.array(groupSchema).max(MAX_ITEMS) }),
]);

const payloadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('question'), summary: summarySchema }),
  z.object({ kind: z.literal('comparison'), comparison: comparisonSchema }),
]);

/** A stored or streamed payload when it is well formed and within bounds, else null. */
export function readChartPayload(raw: unknown): ChartPayload | null {
  const parsed = payloadSchema.safeParse(raw);
  return parsed.success ? parsed.data as ChartPayload : null;
}

/** Answers listed to the model, so long lists stay small. */
const MODEL_ANSWERS = 20;

/** Round for the model: shares to 3 decimals, statistics to 2. */
function round(value: number | null, digits: number): number | null {
  return value === null ? null : Number(value.toFixed(digits));
}

/**
 * The compact numbers the model reads to write its sentence: labels, counts,
 * shares and statistics. Never raw value arrays or respondent free text.
 */
export function describeForModel(payload: ChartPayload): Record<string, unknown> {
  if (payload.kind === 'comparison') {
    const comparison = payload.comparison;
    if (comparison.kind === 'scatter') {
      return { relationship: 'scatter', x: comparison.a.label, y: comparison.b.label, n: comparison.n, pearson: round(comparison.pearson, 3), spearman: round(comparison.spearman, 3) };
    }
    if (comparison.kind === 'groups') {
      return {
        relationship: 'groups', by: comparison.category.label, value: comparison.value.label, n: comparison.n,
        groups: comparison.groups.map(group => ({ label: group.label, n: group.n, median: round(group.median, 2), q1: round(group.q1, 2), q3: round(group.q3, 2), min: round(group.min, 2), max: round(group.max, 2) })),
      };
    }
    return {
      relationship: 'crosstab', rows_question: comparison.a.label, columns_question: comparison.b.label, n: comparison.n,
      columns: comparison.columns.map(column => column.label),
      rows: comparison.rows.map((row, index) => ({ label: row.label, total: comparison.rowTotals[index], counts: comparison.cells[index].map(cell => cell.count) })),
    };
  }
  const summary = payload.summary;
  const common = { question: summary.label, answered: summary.answered, eligible: summary.eligible, excluded: summary.excluded, caption: captionFor(summary) };
  switch (summary.kind) {
    case 'distribution':
      return {
        ...common,
        answers: summary.items.slice(0, MODEL_ANSWERS).map(entry => ({ label: entry.label, count: entry.count, share: round(entry.share, 3) })),
        total_options: summary.items.length + (summary.rest?.options ?? 0),
        returned_options: Math.min(summary.items.length, MODEL_ANSWERS),
        omitted_options: Math.max(0, summary.items.length - MODEL_ANSWERS) + (summary.rest?.options ?? 0),
        ...(summary.rest ? { other_options: summary.rest } : {}),
        ...(summary.multi ? { multi_select: true } : {}),
      };
    case 'numeric':
      return { ...common, stats: { n: summary.n, mean: round(summary.mean, 2), median: round(summary.median, 2), min: round(summary.min, 2), max: round(summary.max, 2), sd: round(summary.stdDev, 2), q1: round(summary.q1, 2), q3: round(summary.q3, 2) } };
    case 'nps':
      return { ...common, nps: Math.round(summary.score), promoters: summary.promoters, passives: summary.passives, detractors: summary.detractors };
    case 'ranking':
      return { ...common, ranking: summary.items.map(entry => ({ label: entry.label, average_rank: round(entry.averageRank, 2), first_place: entry.firstPlace })) };
    case 'matrix':
      return { ...common, rows: summary.rows.map(row => ({ label: row.label, answered: row.answered, counts: row.cells.map(cell => ({ label: cell.label, count: cell.count })) })) };
    case 'timeline':
      return { ...common, granularity: summary.granularity, periods: summary.bins };
    default:
      return common;
  }
}
